import { fetchCached } from '../utils/fetch_cached'
import { CacheStrategy } from '../types/cache'
import { getSelectionFromUrl } from '../utils/get_selection_from_url'
import { buildUrlWithSelection } from '../utils/build_url_with_selection'
import BaseController from './base_controller'

import type { ProductSelectionParams } from '../types/product_selection'

export default class ProductController extends BaseController {
  static readonly SECTION_KEYS = {
    SWATCHES: 'product-ssr-swatches',
    INSEAMS: 'product-ssr-inseams',
    GALLERY: 'product-ssr-gallery',
    INFO: 'product-ssr-info',
    VARIANTS: 'product-ssr-variants',
  } as const

  static values = {
    url: String,
    familyTags: Array,
  }

  declare urlValue: string
  declare familyTagsValue: string[]

  static targets = [
    'artificialVariantLink',
    'artificialVariantsSwatchesContainer',
    'artificialVariantsInseamContainer',
    'productGalleryContainer',
    'productInfoContainer',
    'productVariantsContainer',
  ]

  // Swatch links for artificial variants (e.g. colors that aren't real variants)
  declare artificialVariantLinkTargets: HTMLAnchorElement[]

  // Section containers to update when artificial variant is clicked or on popstate
  declare artificialVariantsSwatchesContainerTarget: HTMLDivElement
  declare artificialVariantsInseamContainerTarget: HTMLDivElement
  declare productGalleryContainerTarget: HTMLDivElement
  declare productInfoContainerTarget: HTMLDivElement
  declare productVariantsContainerTarget: HTMLFormElement

  // AbortController to cancel in-flight requests when user clicks rapidly between variants
  private currentAbortController: AbortController | null = null

  connect(): void {
    // Handle browser back/forward buttons
    window.addEventListener('popstate', this.onPopState)

    // Track size changes to keep URL params in sync
    document.addEventListener('change', this.onSizeChange)

    // Apply URL selection params on initial page load (e.g. shared link with ?size=4)
    requestAnimationFrame(() => {
      this.applySelectionFromUrl()
    })
  }

  disconnect(): void {
    window.removeEventListener('popstate', this.onPopState)
    document.removeEventListener('change', this.onSizeChange)
  }

  onPopState = async (_: PopStateEvent): Promise<void> => {
    const url = window.location.href

    // Content is stale after popstate — re-fetch without pushing to history
    this.updateProduct(url, false)
  }

  /**
   * Listens for size (variant) select changes and updates the URL ?size= param
   * via replaceState so the current selection is reflected in the URL.
   */
  onSizeChange = (event: Event): void => {
    const target = event.target
    if (!(target instanceof HTMLSelectElement)) return
    if (!/options\[/i.test(target.name)) return

    const selectedValue = target.value
    if (!selectedValue) return

    const url = new URL(window.location.href)
    url.searchParams.set('size', selectedValue)
    window.history.replaceState({}, '', url.toString())
  }

  async onArtificialVariantChange(event: Event): Promise<void> {
    event.preventDefault()

    const target = event.currentTarget as HTMLElement
    let urlString = ''

    if (target instanceof HTMLAnchorElement) {
      urlString = target.href
    } else if (target instanceof HTMLSelectElement) {
      urlString = target.value
    } else {
      return
    }

    // Gather current selections to carry forward
    const currentSize = this.getCurrentSizeSelection()
    const currentInseam = this.getCurrentInseamSelection()

    const clickedProductUrl = new URL(urlString, window.location.origin)
    const cleanPath = clickedProductUrl.pathname

    // Build params to carry forward
    const params: Partial<ProductSelectionParams> = {}

    if (target instanceof HTMLAnchorElement) {
      // Swatch (color) change — carry both size and inseam
      if (currentSize) params.size = currentSize
      if (currentInseam) params.inseam = currentInseam
    } else if (
      target instanceof HTMLSelectElement &&
      target.name === 'inseam'
    ) {
      // Inseam change — carry only size (inseam is implicit in the new product URL)
      if (currentSize) params.size = currentSize
    }

    const historyUrl = buildUrlWithSelection(cleanPath, params)
    this.updateProduct(historyUrl)
  }

  private resetAbortController(): AbortSignal {
    this.currentAbortController?.abort()
    this.currentAbortController = new AbortController()
    return this.currentAbortController.signal
  }

  /**
   * Strips selection query params (size, inseam) from the URL before appending
   * Shopify section keys. These params are client-side only and don't affect
   * server-side section rendering.
   */
  private buildSectionFetchUrl(url: string): URL {
    const fetchUrl = new URL(url)
    fetchUrl.searchParams.delete('size')
    fetchUrl.searchParams.delete('inseam')
    fetchUrl.searchParams.append(
      'sections',
      Object.values(ProductController.SECTION_KEYS).join(','),
    )

    return fetchUrl
  }

  private async fetchSections(
    url: string,
    signal: AbortSignal,
  ): Promise<Record<string, string>> {
    const fetchUrl = this.buildSectionFetchUrl(url)
    const response = await fetchCached(fetchUrl.toString(), {
      strategy: CacheStrategy.Long,
      signal,
    })

    if (!response.ok) throw new Error('Failed to load product sections')

    return response.json()
  }

  private handleUpdateError(error: unknown, url: string): void {
    if (error instanceof DOMException && error.name === 'AbortError') return

    console.error('[Product Controller] Error updating product:', error)
    window.location.href = url
  }

  async updateProduct(
    url: string,
    updateHistory: boolean = true,
  ): Promise<void> {
    const signal = this.resetAbortController()

    try {
      if (updateHistory) {
        window.history.pushState({}, '', url)
      }

      const sectionJson = await this.fetchSections(url, signal)

      if (signal.aborted) return

      const { SWATCHES, INSEAMS, GALLERY, INFO, VARIANTS } =
        ProductController.SECTION_KEYS

      const [
        swatchesSectionHtml,
        inseamSectionHtml,
        gallerySectionHtml,
        infoSectionHtml,
        variantsSectionHtml,
      ] = [SWATCHES, INSEAMS, GALLERY, INFO, VARIANTS].map(
        (key) => sectionJson[key],
      )

      await this.replaceSection(
        swatchesSectionHtml,
        inseamSectionHtml,
        gallerySectionHtml,
        infoSectionHtml,
        variantsSectionHtml,
      )

      // Apply stored selections (size, inseam) from URL params to the freshly rendered sections
      this.applySelectionFromUrl()

      this.urlValue = url
    } catch (error) {
      this.handleUpdateError(error, url)
    }
  }

  async replaceSection(
    swatchesHtml: string | null,
    inseamsHtml: string | null,
    galleryHtml: string | null,
    infoHtml: string | null,
    variantsHtml: string | null,
  ): Promise<void> {
    const inject = (target: Element, html: string | null, cb?: () => void) =>
      new Promise<void>((resolve) => {
        this.injectHtmlIntoTarget(target, html, () => {
          cb?.()
          resolve()
        })
      })

    await Promise.all([
      inject(this.artificialVariantsInseamContainerTarget, inseamsHtml),
      inject(this.productGalleryContainerTarget, galleryHtml),
      inject(this.productInfoContainerTarget, infoHtml),
      inject(this.productVariantsContainerTarget, variantsHtml),
      inject(this.artificialVariantsSwatchesContainerTarget, swatchesHtml),
    ])
  }

  // ---------------------------------------------------------------------------
  // URL selection param helpers
  // ---------------------------------------------------------------------------

  /**
   * Reads the currently selected size from the variant <select> in the DOM.
   * Returns null when no size is selected or the select doesn't exist.
   */
  private getCurrentSizeSelection(): string | null {
    const sizeSelect =
      this.productVariantsContainerTarget.querySelector<HTMLSelectElement>(
        'select[name*="options[Size]"], select[name*="options[size]"]',
      )

    return sizeSelect?.value || null
  }

  /**
   * Reads the currently selected inseam *label* (e.g. "26") from the inseam
   * <select>.  We use the option text rather than the value because the value
   * is a product URL.
   */
  private getCurrentInseamSelection(): string | null {
    const inseamSelect =
      this.artificialVariantsInseamContainerTarget.querySelector<HTMLSelectElement>(
        'select[name="inseam"]',
      )

    if (!inseamSelect) return null

    const selected = inseamSelect.selectedOptions[0]
    return selected?.text?.trim() || null
  }

  /**
   * After sections are re-rendered, reads ?size= and ?inseam= from the URL and
   * applies them to the newly rendered selects when matching options exist.
   *
   * Inseam is handled first because changing it triggers a second product fetch
   * (inseams are separate Shopify products).  Size is applied only when the
   * current inseam already matches (or doesn't exist).
   */
  private applySelectionFromUrl(): void {
    const params = getSelectionFromUrl()

    // 1. Inseam — may trigger another product navigation
    if (params.inseam) {
      const navigated = this.applyInseamFromUrl(params.inseam, params.size)
      if (navigated) return // Size will be applied after the next re-render
    }

    // 2. Size — set on the native <select> so that SelectController picks it up
    if (params.size) {
      this.applySizeFromUrl(params.size)
    }
  }

  /**
   * Finds the inseam option whose label matches `targetInseam` and navigates
   * to that product if it differs from the currently selected inseam.
   *
   * Returns `true` when a navigation was triggered (caller should stop).
   */
  private applyInseamFromUrl(
    targetInseam: string,
    targetSize: string | null,
  ): boolean {
    const inseamSelect =
      this.artificialVariantsInseamContainerTarget.querySelector<HTMLSelectElement>(
        'select[name="inseam"]',
      )

    if (!inseamSelect) return false

    // Already on the correct inseam — nothing to do
    const currentOption = inseamSelect.selectedOptions[0]
    if (currentOption && currentOption.text.trim() === targetInseam) {
      return false
    }

    // Find a matching inseam option
    for (const option of Array.from(inseamSelect.options)) {
      if (option.text.trim() === targetInseam) {
        // Navigate to the inseam product, carrying size forward
        const params: Partial<ProductSelectionParams> = {}
        if (targetSize) params.size = targetSize

        const historyUrl = buildUrlWithSelection(option.value, params)
        this.updateProduct(historyUrl)
        return true
      }
    }

    return false // Inseam not available on this product
  }

  /**
   * Sets the size on the native <select> element if the value exists,
   * then syncs the custom SelectController UI (trigger label + list
   * selection state) so the dropdown visually reflects the change.
   *
   * A `change` event is dispatched so the CTA controller can toggle
   * Add to Bag / Notify Me based on the variant's availability.
   */
  private applySizeFromUrl(targetSize: string): void {
    const sizeSelect =
      this.productVariantsContainerTarget.querySelector<HTMLSelectElement>(
        'select[name*="options[Size]"], select[name*="options[size]"]',
      )

    if (!sizeSelect) return

    // Check whether this size exists as an option
    const matchingOption = Array.from(sizeSelect.options).find(
      (o) => o.value === targetSize,
    )
    if (!matchingOption) return

    // Set the native select value
    sizeSelect.value = targetSize
    sizeSelect.dispatchEvent(new Event('change', { bubbles: true }))

    // Sync the custom SelectController UI so the trigger label and
    // listbox selection state match the programmatic value change.
    this.syncSelectController(sizeSelect, targetSize)
  }

  /**
   * Finds the SelectController wrapping a native <select> and tells it
   * to re-read the native value.  Safe to call when no wrapper exists
   * (e.g. in tests without the full snippet markup).
   */
  private syncSelectController(
    nativeSelect: HTMLSelectElement,
    value?: string,
  ): void {
    const wrapper = nativeSelect.closest(
      '[data-controller="select"]',
    ) as HTMLElement | null

    if (!wrapper) return

    const ctrl = this.application.getControllerForElementAndIdentifier(
      wrapper,
      'select',
    )

    if (ctrl && 'syncFromNative' in ctrl) {
      ;(ctrl as { syncFromNative: (v?: string) => void }).syncFromNative(value)
    }
  }
}
